<template>
  <div id="metadata-entity-form" data-test="outside-vif">
    <h2 id="chemical-type-title" v-if="associatedNodeTitle">
      For: {{ associatedNodeTitle }}
    </h2>
    <b-form @submit="submitMetadata">
      <div v-for="fieldData in bundleFieldsSorted" :key="fieldData.field_name">
        <entity-form-field
          v-if="renderField(fieldData.field_name)"
          :field-data="fieldData"
          :parent-value="formData[fieldData.field_name]"
          entity-type="raft_metadata"
          :bundle="getMetadataType"
          :placeholder="getPlaceholder(fieldData.field_name)"
          @disable-form-submit="submitDisabled = true"
          @form-error-message="catchFormError"
          @update-uploading-status="updateUploadingStatus"
          @update-form-data="updateFormData"
          :add-class="addClass"
        />
      </div>
      <b-form-row>
        <b-button
          id="raft-add-metadata"
          type="submit"
          variant="primary"
          :disabled="disableSubmit"
        >
          Submit
        </b-button>
      </b-form-row>
    </b-form>
  </div>
</template>

<script>
import EntityFormField from "../EntityFormField.vue";
import entityFormHelper from "../../lib/EntityFormHelper.js";
import jsonapiClient from "../../lib/jsonapi_client.js";
import MetadataHelper from "../../lib/MetadataHelper";
import raftRestClient from "../../lib/raft_rest_client.js";

export default {
  name: "AddMetadataEntity",
  data() {
    return {
      bundleFieldsSorted: {},
      formData: {},
      submitDisabled: true,
      uploading: false,
      associatedNode: { attributes: { title: "" } },
    };
  },
  props: {
    addClass: { type: String, default: "" },
    metadataType: { type: String, default: null },
    associationUuid: { type: String, default: null },
    associationBundle: { type: String, default: null },
    redirectOnSubmit: { type: Boolean, default: false },
  },
  methods: {
    focusDesired() {
      this.$refs.field_incident_invest_activity?.$el.focus();
      this.$refs.field_inc_invest_action_text?.$el.focus();
    },
    updateUploadingStatus(status) {
      return this.uploading === status;
    },
    updateFormData(value) {
      this.formData[value.field_name] = value.field_value;
      this.submitDisabled = false;
    },
    catchFormError(error) {
      this.$emit("form-error-message", error);
    },
    errorEmit(message, data) {
      this.$emit("form-error-message", { message: message, data: data });
    },
    submitMetadata(event) {
      event.preventDefault();
      const url = "raft_metadata/" + this.getMetadataType;
      const data = entityFormHelper.createJsonApiEntityData(
        "raft_metadata",
        this.getMetadataType,
      );

      data.relationships.association = {
        data: {
          id: this.getAssociationUuid,
          type: "node--" + this.getAssociationBundle,
        },
      };

      entityFormHelper.fillJsonApiDataFromFormData(
        data,
        this.bundleFieldsSorted,
        this.formData,
      );
      MetadataHelper.modifySubmissionData(this.getMetadataType, "add", data);
      jsonapiClient.postJSONapi(
        url,
        data,
        (response) => {
          // Allow the parent element to handle anything needed.
          this.$emit("submit-form-data", response);
          // When on a full page instead of in another component, handle submit.
          if (this.redirectOnSubmit) {
            this.submitDisabled = true;
            this.$showNotification(
              "success",
              MetadataHelper.messages.submitted(this.getMetadataType),
            );
            entityFormHelper.gotoDestination();
          }
        },
        this.errorEmit,
        this,
      );
    },
    renderField: function (fieldName) {
      return MetadataHelper.renderField(this.getMetadataType, fieldName, "add");
    },
    getPlaceholder: function (fieldName) {
      const fieldConfig = this.bundleFieldsSorted.find(
        (item) => item.field_name === fieldName,
      );
      if (fieldConfig) {
        return fieldConfig.form_display.settings.placeholder;
      }
      return MetadataHelper.getPlaceholder(this.getMetadataType, fieldName);
    },
  },
  computed: {
    associatedNodeTitle() {
      return this.associatedNode?.attributes?.title;
    },
    getMetadataType: function () {
      if (this.metadataType) {
        return this.metadataType;
      }
      return this.$route.params.raft_metadata_type;
    },
    getAssociationUuid: function () {
      if (this.associationUuid) {
        return this.associationUuid;
      }
      return this.$route.params.target_uuid;
    },
    getAssociationBundle: function () {
      if (this.associationBundle) {
        return this.associationBundle;
      }
      return this.$route.params.target_bundle;
    },
    disableSubmit: function () {
      if (this.uploading) {
        return true;
      }
      return this.submitDisabled;
    },
  },
  mounted() {
    const that = this;

    // Fetch only the fields that are needed.
    const querystring =
      "fields[node--" +
      this.getAssociationBundle +
      "]=title,drupal_internal__nid";
    jsonapiClient.getJSONapi(
      "node/" + this.getAssociationBundle + "/" + this.getAssociationUuid,
      querystring,
      (data) => {
        that.associatedNode = data.data;
      },
      this.errorEmit,
      this,
    );

    const url = "fields/raft_metadata/" + this.getMetadataType;
    raftRestClient.getRest(
      url,
      {},
      (data) => {
        const keys = Object.keys(data);
        for (const fieldName of keys) {
          that.formData[fieldName] = entityFormHelper.getDefaultValue(
            data[fieldName],
          );
        }
        that.bundleFields = data;
        const bundleFieldsSorted = [];
        Object.values(data).forEach((fieldData) => {
          if (typeof fieldData.form_display !== "undefined") {
            bundleFieldsSorted.push(fieldData);
          }
        });
        bundleFieldsSorted.sort(
          (a, b) => a.form_display.weight - b.form_display.weight,
        );
        that.bundleFieldsSorted = bundleFieldsSorted;
      },
      this.errorEmit,
      this,
    );
    this.focusDesired();
  },
  components: {
    EntityFormField,
  },
};
</script>

<style>
.fullWidth {
  min-width: 100%;
}

textarea {
  font-family: helvetica, arial, sans-serif;
}
</style>
